import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { HashRouter, Routes, Route } from 'react-router-dom';
import PropTypes from 'prop-types';
import _get from 'lodash-es/get';
import _noop from 'lodash-es/noop';

// sushiweb
import ToastContainer from 'sushiweb/ToastContainer';
import Toast from 'sushiweb/Toast';

// helpers
import { isUserLoggedIn } from './utility';
import { closeToast } from './reducers/uiLogic';
import { authCheckInitiate } from './actions/auth';
import { getCompanyDetailsInitiate } from './actions/company';

// Routes
import {
  CREATE_GROUPS,
  EDIT_GROUPS,
  EMPLOYEES_ROUTE,
  EMPLOYEE_DETAILS_ROUTE,
  GROUP_DETAILS_ROUTE,
  ONBOARDING_ROUTE,
  PROGRAMS_ROUTE,
  PROGRAM_DETAILS_ROUTE,
  UPLOAD_EMPLOYEES,
  UPLOAD_EMPLOYEES_STATUS,
  // PAYMENTS_ROUTE,
  HOME_ROUTE,
  EVENT_DETAILS,
  PAYMENTS_ROUTE,
  CHECKOUT_ONLINE_PAY_ROUTE,
  PAYMENT_EVENT_DETAILS,
  HOME_EMPLOYEE_DETAILS_ROUTE,
  INVOICE_DETAILS_ROUTE,
  PAY_INVOICE_ROUTE,
  ONLINE_PAYMENT_SUCCESS,
  REPORTS_ROUTE,
  ADHOC_PAYMENTS,
  ADHOC_PAYMENTS_SUCCESS,
  GIFTING_ROUTE,
  GIFTING_PLAN_INFO,
} from './constants/routes';

// Components
import Loader from './pages/common/components/Loader';
import HeaderNav from './pages/common/components/Header';
import SideNavbar from './pages/common/components/SideNavbar';
import RootComponent from './pages/common/components/RootComponent';
import ProtectedRoute from './pages/common/components/ProtectedRoute';
import GlobalPopups from './pages/common/components/GlobalPopups';

// Pages
import Onboarding from './pages/Onboarding';
import WalletPrograms from './pages/WalletPrograms';
import Program from './pages/Program';
import UploadEmployees from './pages/Employees/components/UploadEmployees';
import Employees from './pages/Employees';
import EmployeeDetails from './pages/EmployeeDetails';
import CreateGroups from './pages/Groups/components/CreateGroups';
import GroupDetails from './pages/GroupDetails';
import UploadStatus from './pages/UploadStatus';
// import Payments from './pages/Payments';
import Dashboard from './pages/Dashboard';
import ErrorPage from './pages/common/components/ErrorPage';
import {
  REFRESH_LABEL,
  applicableFlowTypes,
  currentFlowTypeEnum,
} from './pages/common/components/constants';
import Login from './pages/Login';
import EventDetails from './pages/EventDetails';
import Payments from './pages/Payments';
import OnlinePaymentCheckoutPage from './pages/Payments/OnlinePaymentCheckoutPage';
import InvoiceDetails from './pages/Payments/Invoices/InvoiceDetails';
import OnlinePaymentResponsePage from './pages/Payments/OnlinePaymentResponsePage/OnlinePaymentResponsePage';
import Reports from './pages/Reports';
import ZTrackingClient from './jumbo/ZTrackingClient';
import { FLOW_TYPE } from './jumbo/Constants';
import AdhocPaymentsPage from './pages/Payments/AdhocPaymentsPage';
import AdhocPaymentsSuccessPage from './pages/Payments/AdhocPaymentsPage/AdhocPaymentsSuccessPage/AdhocPaymentsSuccessPage';
import Gifting from './pages/Gifting';
import GiftingProgramInfo from './pages/GiftingProgramInfo';
// import NotFound from './pages/common/components/NotFound';

const AppRoutes = ({
  userDetails,
  // verified,
  currentFlow,
  isLoggedIn,
  companyProfile,
  selectedWorkspace,
  fetchingCompanyProfile,
  fetchedCompanyProfile,
  getCompanyDetails,
  toastDetails,
  removeToast,
  isAuthenticating,
  Authenticated,
  dispatchToAuthenticate,
  isOnlinePaymentsEnabled,
  isReportsTabEnabled,
  isGiftingFlowSupported,
}) => {
  const refreshPage = () => {
    dispatchToAuthenticate();
  };

  useEffect(() => {
    if (!isLoggedIn) {
      dispatchToAuthenticate();
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      getCompanyDetails();
      ZTrackingClient.setId(userDetails.user_id.toString());
      ZTrackingClient.setCompanyId(
        userDetails.selectedWorkspace.company_id ?? '',
      );
      ZTrackingClient.setFlowType(
        currentFlow ? FLOW_TYPE.LIMIT : FLOW_TYPE.PPI,
      );
    }
  }, [isLoggedIn]);

  if (isAuthenticating || fetchingCompanyProfile) {
    return <Loader />;
  }

  if (!Authenticated) {
    return <Login isLoggedIn={isLoggedIn} />;
  }

  if (!fetchedCompanyProfile) {
    return (
      <ErrorPage
        showAction
        actionLabel={REFRESH_LABEL}
        clickAction={refreshPage}
      />
    );
  }
  return (
    <HashRouter>
      <GlobalPopups />
      <ToastContainer>
        <Toast
          show={!!toastDetails.show}
          align='top'
          onClose={removeToast}
          text={toastDetails.text}
          type={toastDetails.type}
        />
      </ToastContainer>
      <HeaderNav
        userDetails={userDetails}
        isLoggedIn={isLoggedIn}
        selectedWorkspace={selectedWorkspace}
        companyProfile={companyProfile}
        currentFlow={currentFlow}
      />
      <Routes>
        <Route
          path={ONBOARDING_ROUTE}
          element={(
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <Onboarding />
            </ProtectedRoute>
          )}
        />
        <Route
          path={UPLOAD_EMPLOYEES}
          element={(
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <UploadEmployees />
            </ProtectedRoute>
          )}
        />
        <Route
          path={CREATE_GROUPS}
          element={(
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <CreateGroups />
            </ProtectedRoute>
          )}
        />
        <Route
          path={EDIT_GROUPS}
          element={(
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <CreateGroups />
            </ProtectedRoute>
          )}
        />
        <Route
          path='/*'
          element={(
            <>
              <SideNavbar
                isLoggedIn={isLoggedIn}
                isOnlinePaymentsEnabled={isOnlinePaymentsEnabled}
                isReportsTabEnabled={isReportsTabEnabled}
                isGiftingFlowSupported={isGiftingFlowSupported}
                currentFlow={currentFlow}
              />
              <Routes>
                <Route
                  path={HOME_ROUTE}
                  element={(
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <Dashboard />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path={PROGRAMS_ROUTE}
                  element={(
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <WalletPrograms />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path={PROGRAM_DETAILS_ROUTE}
                  element={(
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <Program />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path={EMPLOYEES_ROUTE}
                  element={(
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <Employees />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path={EMPLOYEE_DETAILS_ROUTE}
                  element={(
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <EmployeeDetails />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path={HOME_EMPLOYEE_DETAILS_ROUTE}
                  element={(
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <EmployeeDetails />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path={EVENT_DETAILS}
                  element={(
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <EventDetails />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path={PAYMENT_EVENT_DETAILS}
                  element={(
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <EventDetails />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path={UPLOAD_EMPLOYEES_STATUS}
                  element={(
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <UploadStatus />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path={GROUP_DETAILS_ROUTE}
                  element={(
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <GroupDetails />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path={PAYMENTS_ROUTE}
                  element={(
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <Payments />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path={REPORTS_ROUTE}
                  element={(
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <Reports />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path={GIFTING_ROUTE}
                  element={(
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <Gifting />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path={GIFTING_PLAN_INFO}
                  element={(
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <GiftingProgramInfo />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path={CHECKOUT_ONLINE_PAY_ROUTE}
                  element={(
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <OnlinePaymentCheckoutPage />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path={INVOICE_DETAILS_ROUTE}
                  element={(
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <InvoiceDetails />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path={PAY_INVOICE_ROUTE}
                  element={(
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <OnlinePaymentCheckoutPage />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path={ONLINE_PAYMENT_SUCCESS}
                  element={(
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <OnlinePaymentResponsePage />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path={ADHOC_PAYMENTS}
                  element={(
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <AdhocPaymentsPage />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path={ADHOC_PAYMENTS_SUCCESS}
                  element={(
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <AdhocPaymentsSuccessPage />
                    </ProtectedRoute>
                  )}
                />
                <Route path='*' element={<RootComponent isLoggedIn={isLoggedIn} />} />
              </Routes>
            </>
          )}
        />
      </Routes>
    </HashRouter>
  );
};

const mapStateToProps = state => {
  const companyState = _get(state, 'apiState.GET_COMPANY_PROFILE', {});
  const companyProfile = _get(state, 'company', {});
  const isOnlinePaymentsEnabled = _get(
    state,
    'user.supported_features.online_payments_enabled',
    false,
  );
  const isReportsTabEnabled = _get(
    state,
    'user.supported_features.reports_tab_enabled',
    false,
  );
  return {
    companyProfile,
    isLoggedIn:
      isUserLoggedIn(state) && _get(state, 'apiState.AUTH_CHECK.success', true),
    isAuthenticated: _get(state, 'apiState.AUTH_CHECK.success', true),
    userDetails: _get(state, 'user', {}),
    selectedWorkspace: _get(state, 'user.selectedWorkspace', {}),
    isGiftingFlowSupported: _get(
      state,
      'user.selectedWorkspace.applicable_flows',
      [],
    )?.includes(currentFlowTypeEnum.FLOW_TYPE_GIFT_CARD),
    currentFlow: _get(
      state,
      'user.currentFlow',
      currentFlowTypeEnum.FLOW_TYPE_PPI,
    ),
    // verified: _get(state, 'company.verified', false),
    toastDetails: _get(state, 'uiLogic.toastDetails', {}),
    fetchingCompanyProfile:
      _get(companyState, 'isFetching', false) &&
      !_get(companyState, 'isIdle', false),
    fetchedCompanyProfile: _get(companyState, 'success', false),
    isAuthenticating:
      _get(state, 'apiState.AUTH_CHECK.isFetching', true) &&
      !_get(state, 'apiState.AUTH_CHECK.isIdle', false),
    Authenticated: _get(state, 'apiState.AUTH_CHECK.success', true),
    isOnlinePaymentsEnabled,
    isReportsTabEnabled,
  };
};

const mapDispatchToProps = dispatch => ({
  removeToast: () => dispatch(closeToast()),
  getCompanyDetails: () => dispatch(getCompanyDetailsInitiate()),
  dispatchToAuthenticate: () => dispatch(authCheckInitiate()),
});

AppRoutes.propTypes = {
  userDetails: PropTypes.objectOf(PropTypes.any),
  selectedWorkspace: PropTypes.objectOf(PropTypes.any),
  // verified: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  getCompanyDetails: PropTypes.func,
  fetchingCompanyProfile: PropTypes.bool,
  fetchedCompanyProfile: PropTypes.bool,
  isAuthenticating: PropTypes.bool,
  Authenticated: PropTypes.bool,
  toastDetails: PropTypes.shape({
    show: PropTypes.bool,
    text: PropTypes.string,
    type: PropTypes.string,
  }).isRequired,
  removeToast: PropTypes.func.isRequired,
  dispatchToAuthenticate: PropTypes.func.isRequired,
  companyProfile: PropTypes.objectOf(PropTypes.any),
  isOnlinePaymentsEnabled: PropTypes.bool,
  isReportsTabEnabled: PropTypes.bool,
  isGiftingFlowSupported: PropTypes.bool,
  currentFlow: PropTypes.oneOf(applicableFlowTypes),
};

AppRoutes.defaultProps = {
  userDetails: {},
  isLoggedIn: false,
  selectedWorkspace: {},
  fetchingCompanyProfile: false,
  fetchedCompanyProfile: false,
  // verified: false,
  isAuthenticating: true,
  Authenticated: false,
  getCompanyDetails: _noop,
  companyProfile: {},
  isOnlinePaymentsEnabled: false,
  isReportsTabEnabled: false,
  isGiftingFlowSupported: false,
  currentFlow: currentFlowTypeEnum.FLOW_TYPE_PPI,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AppRoutes);
